import React, { useEffect, useRef, useState } from "react";
import { Play, X, Video } from "lucide-react";
import { useVideo } from "@/lib/videos-context";

/**
 * Public video embed.
 * - Fetches youtubeId from VideosProvider.
 * - Auto-plays MUTED when scrolled into view (IntersectionObserver).
 * - Pauses when out of view by unmounting the iframe.
 */
export default function VideoEmbed({ storageKey, posterImage, aspect = "video", label = "Vídeo" }) {
  const { youtubeId, loading } = useVideo(storageKey);
  const [inView, setInView] = useState(false);
  const [unmuted, setUnmuted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.35, rootMargin: "0px 0px -10% 0px" }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const ratio = aspect === "portrait" ? "aspect-[4/5]" : aspect === "shorts" ? "aspect-[9/16]" : "aspect-video";

  const showPlayer = inView && !!youtubeId;
  const muteParam = unmuted ? 0 : 1;
  const embedSrc = youtubeId
    ? `https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=${muteParam}&rel=0&playsinline=1&loop=1&playlist=${youtubeId}&modestbranding=1`
    : "";

  return (
    <div
      ref={ref}
      className={`relative w-full ${ratio} rounded-[2rem] overflow-hidden bg-stone-900 group`}
      data-testid={`video-embed-${storageKey}`}
    >
      {showPlayer ? (
        <>
          <iframe
            key={`${storageKey}-${unmuted}`}
            src={embedSrc}
            title={label}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
          {!unmuted && (
            <button
              onClick={() => setUnmuted(true)}
              className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-black/70 hover:bg-black/85 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg transition-all"
              aria-label="Ativar som"
              data-testid={`video-unmute-${storageKey}`}
            >
              <Play className="w-3 h-3 fill-white" /> Ativar som
            </button>
          )}
        </>
      ) : (
        <>
          {posterImage ? (
            <img src={posterImage} alt={label} className="w-full h-full object-cover opacity-90" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-stone-800 via-stone-900 to-stone-800" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-stone-950/20 to-stone-950/30" />

          {youtubeId ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-20 w-20 rounded-full bg-orange-600 flex items-center justify-center shadow-[0_20px_50px_rgba(234,88,12,0.5)]">
                <Play className="w-9 h-9 text-white fill-white ml-1" />
              </div>
            </div>
          ) : (
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-black/50 backdrop-blur px-3 py-1.5 rounded-full text-white text-xs font-semibold">
              <Video className="w-3.5 h-3.5 text-orange-400" /> {loading ? "Carregando..." : "Vídeo em breve"}
            </div>
          )}
        </>
      )}
    </div>
  );
}
